// src/router/routes.jsx
import React, { Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import RouteGuard from './RouteGuard';
import ForgotPassword from '@/pages/auth/ForgotPassword/ForgotPassword.jsx';
import { useAuthStore } from '@/store/useAuthStore';
import { USER_ROLES } from '@/config/permissions';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import {
  loadApp,
  loadLogin,
  loadSignOn,
  loadResetPassword,
  loadAcceptFreelanceInvitation,
  loadHandbook,
  loadDevicesTools,
  loadFaq,
  loadFiles,
  loadClients,
  loadClientsLayout,
  loadClientDetail,
  loadFreelancersLayout,
  loadFreelancers,
  loadFreelancerDetail,
  loadMissionsLayout,
  loadMissions,
  loadMissionCreate,
  loadMissionEdit,
  loadUsersLayout,
  loadUsers,
  loadTagManagement,
  loadActivityReportSummary,
  loadAudit,
  loadActivityReportLayout,
  loadActivityReport,
  loadActivityReportCreate,
  loadActivityReportView,
  loadActivityReportInvoicePreview,
  loadAvailability,
  loadOurPartners,
  loadAccounting,
  loadVerify,
  loadFreelancerOnboarding,
  loadCompanyOnboarding,
  loadSettings,
  loadBillingSuccess,
  loadBillingCancel,
  loadAuthCallback,
  loadSuperAdmin,
  loadSuperAdminKYCDocuments,
  loadSuperAdminTemplates,
  loadSuperAdminBlog,
  loadSuperAdminBlogPostForm,
  loadSuperAdminStaffedFree,
  loadSuperAdminEInvoicing,
  loadSuperAdminEmails,
  loadTemplateEditPage,
  loadActivityReportClientValidation,
  loadAvailabilityRespond,
  loadPortageQuestionnaire,
  loadPortageMission,
  loadClientContract,
  loadClientDashboard,
  loadNotFound,
} from './routeLoaders';
import { AppLoadingScreen } from '@/components/loading/AppLoadingScreen';

const App = lazyWithRetry(loadApp);
const Login = lazyWithRetry(loadLogin);
const SignOn = lazyWithRetry(loadSignOn);
const ResetPassword = lazyWithRetry(loadResetPassword);
const AcceptFreelanceInvitation = lazyWithRetry(loadAcceptFreelanceInvitation);
const Handbook = lazyWithRetry(loadHandbook);
const DevicesTools = lazyWithRetry(loadDevicesTools);
const FaqPage = lazyWithRetry(loadFaq);
const Files = lazyWithRetry(loadFiles);
const Clients = lazyWithRetry(loadClients);
const ClientsLayout = lazyWithRetry(loadClientsLayout);
const ClientDetail = lazyWithRetry(loadClientDetail);
const FreelancersLayout = lazyWithRetry(loadFreelancersLayout);
const Freelancers = lazyWithRetry(loadFreelancers);
const FreelancerDetail = lazyWithRetry(loadFreelancerDetail);
const MissionsLayout = lazyWithRetry(loadMissionsLayout);
const Missions = lazyWithRetry(loadMissions);
const MissionCreate = lazyWithRetry(loadMissionCreate);
const MissionEdit = lazyWithRetry(loadMissionEdit);
const UsersLayout = lazyWithRetry(loadUsersLayout);
const Users = lazyWithRetry(loadUsers);
const TagManagement = lazyWithRetry(loadTagManagement);
const ActivityReportSummary = lazyWithRetry(loadActivityReportSummary);
const Audit = lazyWithRetry(loadAudit);
const ActivityReportLayout = lazyWithRetry(loadActivityReportLayout);
const ActivityReport = lazyWithRetry(loadActivityReport);
const ActivityReportCreate = lazyWithRetry(loadActivityReportCreate);
const ActivityReportView = lazyWithRetry(loadActivityReportView);
const ActivityReportInvoicePreview = lazyWithRetry(loadActivityReportInvoicePreview);
const Availability = lazyWithRetry(loadAvailability);
const OurPartners = lazyWithRetry(loadOurPartners);
const Accounting = lazyWithRetry(loadAccounting);
const Verify = lazyWithRetry(loadVerify);
const FreelancerOnboarding = lazyWithRetry(loadFreelancerOnboarding);
const CompanyOnboarding = lazyWithRetry(loadCompanyOnboarding);
const Settings = lazyWithRetry(loadSettings);
const BillingSuccess = lazyWithRetry(loadBillingSuccess);
const BillingCancel = lazyWithRetry(loadBillingCancel);
const AuthCallback = lazyWithRetry(loadAuthCallback);
const SuperAdmin = lazyWithRetry(loadSuperAdmin);
const SuperAdminKYCDocuments = lazyWithRetry(loadSuperAdminKYCDocuments);
const SuperAdminTemplates = lazyWithRetry(loadSuperAdminTemplates);
const SuperAdminBlog = lazyWithRetry(loadSuperAdminBlog);
const SuperAdminBlogPostForm = lazyWithRetry(loadSuperAdminBlogPostForm);
const SuperAdminStaffedFree = lazyWithRetry(loadSuperAdminStaffedFree);
const SuperAdminEInvoicing = lazyWithRetry(loadSuperAdminEInvoicing);
const SuperAdminEmails = lazyWithRetry(loadSuperAdminEmails);
const TemplateEditPage = lazyWithRetry(loadTemplateEditPage);
const ActivityReportClientValidation = lazyWithRetry(loadActivityReportClientValidation);
const AvailabilityRespond = lazyWithRetry(loadAvailabilityRespond);
const PortageQuestionnaire = lazyWithRetry(loadPortageQuestionnaire);
const PortageMission = lazyWithRetry(loadPortageMission);
const ClientContract = lazyWithRetry(loadClientContract);
const ClientDashboard = lazyWithRetry(loadClientDashboard);
const NotFound = lazyWithRetry(loadNotFound);

function TeamAdminRedirect() {
  const location = useLocation();
  return <Navigate to={`/signup/company${location.search}`} replace />;
}

function PortageQuestionnaireRedirect() {
  const location = useLocation();
  return <Navigate to={`/portage/onboarding${location.search}`} replace />;
}

function RegisterRedirect() {
  const location = useLocation();
  return <Navigate to={`/signup/company${location.search}`} replace />;
}

function DefaultRedirect() {
  const sessionDefaultPath = useAuthStore((state) => state.sessionDefaultPath);
  const sessionRequiredPath = useAuthStore((state) => state.sessionRequiredPath);
  const { globalRole: role } = useAuthStore();

  if (sessionRequiredPath) {
    return <Navigate to={sessionRequiredPath} replace state={{ isEdit: false }} />;
  }

  if (sessionDefaultPath) {
    return <Navigate to={sessionDefaultPath} replace />;
  }

  if (role === USER_ROLES.SUPER_ADMIN) {
    return <Navigate to="/super-admin" replace />;
  }

  if (role === USER_ROLES.FREELANCER) {
    return <Navigate to="/missions" replace />;
  }

  return <Navigate to="/freelancers" replace />;
}

function withSuspense(Component, fallback = null) {
  return (
    <Suspense fallback={fallback}>
      <Component />
    </Suspense>
  );
}

const routes = [
  {
    path: '/',
    element: <RouteGuard>{withSuspense(App)}</RouteGuard>,
    meta: { breadcrumb: 'Folkyn' },
    children: [
      {
        index: true,
        element: <DefaultRedirect />,
      },
      { path: 'settings', element: withSuspense(Settings), meta: { breadcrumb: 'settings.title' } },
      {
        path: 'billing/success',
        element: withSuspense(BillingSuccess),
        meta: { breadcrumb: 'billing.success.metaTitle' },
      },
      {
        path: 'billing/cancel',
        element: withSuspense(BillingCancel),
        meta: { breadcrumb: 'billing.cancel.metaTitle' },
      },
      {
        path: 'templates/new',
        element: withSuspense(TemplateEditPage),
        meta: { breadcrumb: 'settingsTemplates.edit.titleNew', parent: '/settings?tab=template' },
      },
      {
        path: 'templates/:id',
        element: withSuspense(TemplateEditPage),
        meta: { breadcrumb: 'settingsTemplates.edit.titleEdit', dynamic: true, parent: '/settings?tab=template' },
      },
      { path: 'assets', element: withSuspense(Handbook), meta: { breadcrumb: 'header.assets' } },
      { path: 'devices-tools', element: withSuspense(DevicesTools), meta: { breadcrumb: 'header.devicesTools' } },
      { path: 'faq', element: withSuspense(FaqPage), meta: { breadcrumb: 'header.faq' } },
      { path: 'files', element: withSuspense(Files), meta: { breadcrumb: 'header.files' } },
      {
        path: 'clients',
        element: withSuspense(ClientsLayout),
        meta: { breadcrumb: 'clients.title' },
        children: [
          {
            index: true,
            element: withSuspense(Clients),
            meta: { breadcrumb: 'clients.title', hideInBreadcrumb: true },
          },
          {
            path: ':clientId',
            element: withSuspense(ClientDetail),
            meta: { breadcrumb: 'clients.detail.title', dynamic: true, parent: '/clients' },
          },
        ],
      },
      {
        path: 'freelancers',
        element: withSuspense(FreelancersLayout),
        meta: { breadcrumb: 'freelancers.title' },
        children: [
          {
            index: true,
            element: withSuspense(Freelancers),
            meta: { breadcrumb: 'freelancers.title', hideInBreadcrumb: true },
          },
          {
            path: ':id',
            element: withSuspense(FreelancerDetail),
            meta: { breadcrumb: 'freelancers.detail.title', dynamic: true, parent: '/freelancers' },
          },
        ],
      },
      {
        path: 'missions',
        element: withSuspense(MissionsLayout),
        meta: { breadcrumb: 'missions.title' },
        children: [
          {
            index: true,
            element: withSuspense(Missions),
            meta: { breadcrumb: 'missions.title', hideInBreadcrumb: true },
          },
          {
            path: 'create',
            element: withSuspense(MissionCreate),
            meta: { breadcrumb: 'missionsCreate.breadcrumbTitle', parent: '/missions' },
          },
          {
            path: 'create/:missionId',
            element: withSuspense(MissionCreate),
            meta: { breadcrumb: 'missionsEdit.breadcrumbTitle', dynamic: true, parent: '/missions' },
          },
          {
            path: 'edit/:missionId',
            element: withSuspense(MissionEdit),
            meta: { breadcrumb: 'missionsEdit.breadcrumbTitle', dynamic: true, parent: '/missions' },
          },
          {
            path: ':missionId',
            element: withSuspense(MissionEdit),
            meta: { breadcrumb: 'missionsEdit.breadcrumbTitle', dynamic: true, parent: '/missions' },
          },
        ],
      },
      {
        path: 'activity-report',
        element: withSuspense(ActivityReportLayout),
        meta: { breadcrumb: 'activityReport.title' },
        children: [
          {
            index: true,
            element: withSuspense(ActivityReport),
            meta: { breadcrumb: 'activityReport.title', hideInBreadcrumb: true },
          },
          {
            path: 'create/:id?',
            element: withSuspense(ActivityReportCreate),
            meta: { breadcrumb: 'activityReport.create.title', dynamic: true, parent: '/activity-report' },
          },
          {
            path: 'view/:id',
            element: withSuspense(ActivityReportView),
            meta: { breadcrumb: 'activityReport.view.title', dynamic: true, parent: '/activity-report' },
          },
        ],
      },
      { path: 'reporting', element: withSuspense(ActivityReportSummary), meta: { breadcrumb: 'reporting.title' } },
      {
        path: 'audit',
        element: withSuspense(Audit),
        meta: { breadcrumb: 'audit.title' },
      },
      {
        path: 'availability',
        element: withSuspense(Availability),
        meta: { breadcrumb: 'availability.title' },
      },
      {
        path: 'our-partners',
        element: withSuspense(OurPartners),
        meta: { breadcrumb: 'ourPartners.metaTitle' },
      },
      {
        path: 'accounting',
        element: withSuspense(Accounting),
        meta: { breadcrumb: 'accounting.title' },
      },
      {
        path: 'users',
        element: withSuspense(UsersLayout),
        meta: { breadcrumb: 'users.title' },
        children: [
          {
            index: true,
            element: withSuspense(Users),
            meta: { breadcrumb: 'users.title', hideInBreadcrumb: true },
          },
          {
            path: 'tag-management',
            element: withSuspense(TagManagement),
            meta: { breadcrumb: 'tagManagement.title', parent: '/users' },
          },
        ],
      },
      {
        path: 'super-admin',
        element: withSuspense(SuperAdmin),
        meta: { breadcrumb: 'superAdmin.title' },
      },
      {
        path: 'super-admin/kyc',
        element: withSuspense(SuperAdminKYCDocuments),
        meta: { breadcrumb: 'superAdmin.kyc.title' },
      },
      {
        path: 'super-admin/templates',
        element: withSuspense(SuperAdminTemplates),
        meta: { breadcrumb: 'superAdmin.templates.title' },
      },
      {
        path: 'super-admin/blog',
        element: withSuspense(SuperAdminBlog),
        meta: { breadcrumb: 'superAdmin.blog.title' },
      },
      {
        path: 'super-admin/blog/:postId',
        element: withSuspense(SuperAdminBlogPostForm),
        meta: { breadcrumb: 'superAdmin.blog.page.breadcrumbForm', dynamic: true, parent: '/super-admin/blog' },
      },
      {
        path: 'super-admin/staffed-free',
        element: withSuspense(SuperAdminStaffedFree),
        meta: { breadcrumb: 'superAdmin.staffedFree.tab' },
      },
      {
        path: 'super-admin/e-invoicing',
        element: withSuspense(SuperAdminEInvoicing),
        meta: { breadcrumb: 'superAdmin.eInvoicing.title' },
      },
      {
        path: 'super-admin/emails',
        element: withSuspense(SuperAdminEmails),
        meta: { breadcrumb: 'superAdmin.emails.title' },
      },
      {
        path: 'super-admin/billing-mandates',
        element: <Navigate to="/super-admin/e-invoicing" replace />,
      },
      {
        path: 'super-admin/invoice-emission-jobs',
        element: <Navigate to="/super-admin/e-invoicing?tab=invoices" replace />,
      },
      {
        path: 'super-admin/templates/new',
        element: withSuspense(TemplateEditPage),
        meta: { breadcrumb: 'settingsTemplates.edit.titleNew', parent: '/super-admin/templates' },
      },
      {
        path: 'super-admin/templates/:id',
        element: withSuspense(TemplateEditPage),
        meta: { breadcrumb: 'settingsTemplates.edit.titleEdit', dynamic: true, parent: '/super-admin/templates' },
      },
    ],
  },
  {
    path: '/company-onboarding',
    element: withSuspense(CompanyOnboarding),
    meta: { breadcrumb: 'companyOnboarding.title', hideInBreadcrumb: true },
  },
  {
    path: '/login',
    element: withSuspense(Login),
    meta: { breadcrumb: 'login.title', hideInBreadcrumb: true },
  },
  {
    path: '/forgot-password',
    element: withSuspense(ForgotPassword),
    meta: { breadcrumb: 'forgotPassword.title', hideInBreadcrumb: true },
  },
  {
    path: '/reset-password',
    element: withSuspense(ResetPassword),
    meta: { breadcrumb: 'resetPassword.title', hideInBreadcrumb: true },
  },
  {
    path: '/sign-on',
    element: withSuspense(SignOn),
    meta: { breadcrumb: 'signOn.title', hideInBreadcrumb: true },
  },
  {
    path: '/signup/company',
    element: withSuspense(SignOn),
    meta: { breadcrumb: 'signupCompany.title', hideInBreadcrumb: true },
  },
  {
    path: '/accept-freelance-invitation',
    element: withSuspense(AcceptFreelanceInvitation),
    meta: { breadcrumb: 'auth.acceptInvitation.title', hideInBreadcrumb: true },
  },
  {
    path: '/auth/callback',
    element: withSuspense(AuthCallback, <AppLoadingScreen />),
    meta: { breadcrumb: 'auth.callback', hideInBreadcrumb: true },
  },
  {
    path: '/register/team-admin',
    element: <TeamAdminRedirect />,
    meta: { breadcrumb: 'register.teamAdmin', hideInBreadcrumb: true },
  },
  {
    path: '/register',
    element: <RegisterRedirect />,
    meta: { breadcrumb: 'register.teamAdmin', hideInBreadcrumb: true },
  },
  {
    path: '/activity-report/client-validation',
    element: withSuspense(ActivityReportClientValidation),
    meta: { breadcrumb: 'activityReport.clientValidation.title', hideInBreadcrumb: true },
  },
  {
    path: '/availability/respond',
    element: withSuspense(AvailabilityRespond),
    meta: { breadcrumb: 'availability.respond.title', hideInBreadcrumb: true },
  },
  {
    path: '/portage/onboarding',
    element: withSuspense(PortageQuestionnaire),
    meta: { breadcrumb: 'portageQuestionnaire.title', hideInBreadcrumb: true },
  },
  {
    path: '/portage/questionnaire',
    element: <PortageQuestionnaireRedirect />,
    meta: { breadcrumb: 'portageQuestionnaire.title', hideInBreadcrumb: true },
  },
  {
    path: '/portage/mission',
    element: withSuspense(PortageMission),
    meta: { breadcrumb: 'portageMission.title', hideInBreadcrumb: true },
  },
  {
    path: '/client/contract',
    element: withSuspense(ClientContract),
    meta: { breadcrumb: 'clientContract.page.title', hideInBreadcrumb: true },
  },
  {
    path: '/client/dashboard',
    element: withSuspense(ClientDashboard),
    meta: { breadcrumb: 'clientDashboard.pageTitle', hideInBreadcrumb: true },
  },
  {
    path: '/activity-report/preview/invoice/:id',
    element: withSuspense(ActivityReportInvoicePreview),
    meta: { breadcrumb: 'activityReport.preview.invoice', hideInBreadcrumb: true },
  },
  {
    path: '/activity-report/preview/expense/:id',
    element: withSuspense(ActivityReportInvoicePreview),
    meta: { breadcrumb: 'activityReport.preview.expense', hideInBreadcrumb: true },
  },
  {
    path: '/onboarding',
    meta: { breadcrumb: 'onboarding.title', hideInBreadcrumb: true },
    children: [
      { path: 'verify', element: withSuspense(Verify), meta: { breadcrumb: 'onboarding.verify', parent: '/onboarding' } },
      { path: 'freelancer', element: withSuspense(FreelancerOnboarding), meta: { breadcrumb: 'freelancerOnboarding.title', parent: '/onboarding' } },
    ],
  },
  {
    path: '*',
    element: withSuspense(NotFound),
    meta: { breadcrumb: 'notFound.title', hideInBreadcrumb: true },
  },
];

export default routes;
